import type { Locale } from '@/lib/i18n/config'
import type { Period } from '@/lib/data/types'

import { intlTag } from '@/lib/i18n/config'

export const EMPTY = '—'

export interface Formatter {
    locale: Locale
    tag: string
    num: (value: number | null | undefined, digits?: number) => string
    pct: (value: number | null | undefined, digits?: number) => string
    pp: (value: number | null | undefined, digits?: number) => string
    signed: (value: number | null | undefined, digits?: number) => string
    money: (value: number | null | undefined, currency: string, compact?: boolean) => string
    compact: (value: number | null | undefined) => string
    date: (ts: number | null | undefined, style?: 'short' | 'medium' | 'long') => string
    monthYear: (ts: number | null | undefined, style?: 'short' | 'long') => string
    month: (ts: number | null | undefined) => string
    period: (period: Period | null | undefined) => string
    days: (value: number) => string
    list: (items: string[]) => string
}

const valid = (value: number | null | undefined): value is number => typeof value === 'number' && Number.isFinite(value)

const cache = new Map<string, Formatter>()

/** Locale-bound formatters. Dates are UTC midnight timestamps. */
export const createFormatter = (locale: Locale): Formatter => {
    const cached = cache.get(locale)

    if (cached) return cached

    const tag = intlTag(locale)
    const numbers = new Map<number, Intl.NumberFormat>()
    const moneyFormats = new Map<string, Intl.NumberFormat>()

    const numberFormat = (digits: number): Intl.NumberFormat => {
        let format = numbers.get(digits)

        if (!format) {
            format = new Intl.NumberFormat(tag, { minimumFractionDigits: digits, maximumFractionDigits: digits })
            numbers.set(digits, format)
        }

        return format
    }

    const compactFormat = new Intl.NumberFormat(tag, { notation: 'compact', maximumFractionDigits: 1 })
    const dateShort = new Intl.DateTimeFormat(tag, { timeZone: 'UTC', day: '2-digit', month: 'short' })
    const dateMedium = new Intl.DateTimeFormat(tag, { timeZone: 'UTC', day: '2-digit', month: 'short', year: 'numeric' })
    const dateLong = new Intl.DateTimeFormat(tag, { timeZone: 'UTC', day: 'numeric', month: 'long', year: 'numeric' })
    const monthShort = new Intl.DateTimeFormat(tag, { timeZone: 'UTC', month: 'short', year: 'numeric' })
    const monthLong = new Intl.DateTimeFormat(tag, { timeZone: 'UTC', month: 'long', year: 'numeric' })
    const monthOnly = new Intl.DateTimeFormat(tag, { timeZone: 'UTC', month: 'short' })
    const listFormat = new Intl.ListFormat(tag, { style: 'long', type: 'conjunction' })

    const num = (value: number | null | undefined, digits = 0): string => (valid(value) ? numberFormat(digits).format(value) : EMPTY)

    const formatter: Formatter = {
        locale,
        tag,
        num,
        pct: (value, digits = 0) => (valid(value) ? `${numberFormat(digits).format(value)}%` : EMPTY),
        pp: (value, digits = 1) => {
            if (!valid(value)) return EMPTY

            const rounded = Number(value.toFixed(digits))
            const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : ''

            return `${sign}${numberFormat(digits).format(Math.abs(rounded))} pp`
        },
        signed: (value, digits = 0) => {
            if (!valid(value)) return EMPTY

            const rounded = Number(value.toFixed(digits))
            const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : ''

            return `${sign}${numberFormat(digits).format(Math.abs(rounded))}`
        },
        money: (value, currency, compact = false) => {
            if (!valid(value)) return EMPTY

            const key = `${currency}|${compact}`
            let format = moneyFormats.get(key)

            if (!format) {
                try {
                    format = new Intl.NumberFormat(tag, {
                        style: 'currency',
                        currency,
                        currencyDisplay: 'narrowSymbol',
                        notation: compact ? 'compact' : 'standard',
                        maximumFractionDigits: compact ? 1 : 0,
                        minimumFractionDigits: 0
                    })
                }
                catch {
                    format = new Intl.NumberFormat(tag, { notation: compact ? 'compact' : 'standard', maximumFractionDigits: compact ? 1 : 0 })
                }

                moneyFormats.set(key, format)
            }

            return `${format.format(value)} ${currency}`
        },
        compact: value => (valid(value) ? compactFormat.format(value) : EMPTY),
        date: (ts, style = 'medium') => {
            if (!valid(ts)) return EMPTY
            if (style === 'short') return dateShort.format(ts)
            if (style === 'long') return dateLong.format(ts)

            return dateMedium.format(ts)
        },
        monthYear: (ts, style = 'short') => {
            if (!valid(ts)) return EMPTY

            return style === 'long' ? monthLong.format(ts) : monthShort.format(ts)
        },
        month: ts => (valid(ts) ? monthOnly.format(ts) : EMPTY),
        period: period => {
            if (!period) return EMPTY

            switch (period.kind) {
                case 'month':
                    return monthShort.format(period.start)
                case 'quarter': {
                    const date = new Date(period.start)
                    const quarter = Math.floor(date.getUTCMonth() / 3) + 1

                    return `${locale === 'en' ? 'Q' : 'T'}${quarter} ${date.getUTCFullYear()}`
                }
                case 'year':
                    return String(new Date(period.start).getUTCFullYear())
                case 'day':
                    return dateMedium.format(period.start)
                default:
                    return period.raw || EMPTY
            }
        },
        days: value => {
            const rounded = Math.round(value)

            if (locale === 'en') return `${num(rounded)} ${Math.abs(rounded) === 1 ? 'day' : 'days'}`

            return `${num(rounded)} ${Math.abs(rounded) === 1 ? 'día' : 'días'}`
        },
        list: items => listFormat.format(items)
    }

    cache.set(locale, formatter)

    return formatter
}
